package fr.frux.core.network

import fr.frux.core.common.auth.SessionExpiryNotifier
import fr.frux.core.common.auth.TokenStorage
import fr.frux.core.common.auth.UnauthorizedException
import fr.frux.core.common.config.apiBaseUrl
import fr.frux.core.network.api.AuthApiImpl
import fr.frux.core.network.auth.FruxTokenRefresh
import fr.frux.core.network.auth.TokenRefresher
import fr.frux.core.network.auth.installAuth
import fr.frux.shared.content.ClientFeatures
import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.engine.HttpClientEngine
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.HttpRequestRetry
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.request.header
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import fr.frux.core.common.logging.Logger as FruxLogger

/**
 * Unique fabrique du HttpClient Frux — utilisée par la DI prod (NetworkModule)
 * ET par la suite de contrats (:tests:contract-tests), pour que les tests
 * prouvent le comportement de la config réellement livrée.
 *
 * [refreshEngine] n'existe que pour les tests (#26, ADR 0019) : `null` en production laisse le
 * client minimal dédié au refresh choisir le moteur par défaut de la plateforme, un test peut y
 * poser le même `MockEngine` que le client principal pour piloter `/auth/refresh` de façon
 * déterministe sans dépendre du réseau.
 */
fun createFruxHttpClient(
    tokenStorage: TokenStorage,
    baseUrl: String = apiBaseUrl(),
    enableLogging: Boolean = true,
    sessionExpiryNotifier: SessionExpiryNotifier = SessionExpiryNotifier(),
    refreshEngine: HttpClientEngine? = null,
): HttpClient =
    HttpClient {
        installFruxDefaults(tokenStorage, baseUrl, enableLogging, sessionExpiryNotifier, refreshEngine)
    }

/**
 * La configuration elle-même, séparée de la fabrique pour qu'un test puisse la monter sur un
 * moteur simulé : autrement il éprouverait une configuration parallèle, pas celle qui est livrée.
 */
fun HttpClientConfig<*>.installFruxDefaults(
    tokenStorage: TokenStorage,
    baseUrl: String = apiBaseUrl(),
    enableLogging: Boolean = true,
    sessionExpiryNotifier: SessionExpiryNotifier = SessionExpiryNotifier(),
    refreshEngine: HttpClientEngine? = null,
) {
    install(ContentNegotiation) {
        json(
            Json {
                prettyPrint = true
                isLenient = true
                ignoreUnknownKeys = true
                // #253/#157, résilience aux kinds futurs : `ignoreUnknownKeys` ne couvre que des clés
                // inconnues, pas une *valeur* d'enum inconnue (ex. un nouveau `NotificationKindDto`
                // ajouté après la sortie de cette version). Sans coercion, une seule notification d'un
                // kind non reconnu ferait échouer le décodage de toute la liste (badge + Activité
                // morts). Avec, la valeur retombe sur le défaut déclaré du champ (`UNKNOWN`).
                coerceInputValues = true
            },
        )
    }

    // Les lignes passent par le Logger Frux, pas `Logger.DEFAULT` (#276) : le point d'entrée de
    // chaque plateforme pose `minLevel = ERROR` en release, donc les URLs complètes (lat/lon/radius
    // des geo-queries) ne sortent plus dans logcat, NSLog ou la console d'un build livré — même
    // décision debug/release que Flocon, sans second interrupteur à câbler.
    if (enableLogging) {
        install(Logging) {
            logger =
                object : Logger {
                    override fun log(message: String) {
                        FruxLogger.d("HttpClient", message)
                    }
                }
            level = LogLevel.INFO
        }
    }

    installFlocon()

    // Délais explicites. Sans ce plugin, le moteur JS (`fetch` sans AbortController) n'expire
    // *jamais* : une requête partie dans le vide laisse l'écran en chargement indéfiniment, sans
    // erreur affichable et sans possibilité de réessayer. Les moteurs Android et iOS ont bien des
    // défauts, mais longs et non maîtrisés — c'est la panne réseau la plus banale, et c'était la
    // seule qui n'aboutissait à aucun message.
    //
    // 15 s de bout en bout : au-dessus du pire cas mesuré côté carte (500 arbres, ~259 Ko), et en
    // dessous du seuil où l'utilisateur a déjà conclu que l'application est cassée.
    install(HttpTimeout) {
        requestTimeoutMillis = 15_000
        connectTimeoutMillis = 10_000
        socketTimeoutMillis = 15_000
    }

    // Réessais sur les seules requêtes idempotentes — `retryOnServerErrors` ne couvre que les 5xx,
    // et `GET`/`HEAD` sont les seules méthodes que rejouer ne peut pas dupliquer. Un POST rejoué
    // créerait un deuxième arbre ou une deuxième inscription : les mutations restent exclues, leur
    // reprise appartient à l'utilisateur.
    //
    // Volontairement muet sur le 429 : `AppError.RateLimited` porte déjà `Retry-After` jusqu'à
    // l'écran, et réessayer automatiquement sous quota reviendrait à creuser le trou.
    //
    // `/auth/refresh` n'a pas besoin d'en être exclu explicitement (#26) : il ne transite jamais
    // par CE client, uniquement par le client minimal dédié construit ci-dessous.
    install(HttpRequestRetry) {
        maxRetries = 2
        retryIf { request, response ->
            response.status.value in 500..599 &&
                request.method in listOf(HttpMethod.Get, HttpMethod.Head)
        }
        retryOnExceptionIf { request, cause ->
            cause !is UnauthorizedException && request.method in listOf(HttpMethod.Get, HttpMethod.Head)
        }
        exponentialDelay(base = 2.0, maxDelayMs = 4_000)
    }

    // Sessions révocables (#26, ADR 0019) : `tokenRefresher` est partagé entre le proactif
    // (AuthInterceptor, avant l'envoi) et le réactif (FruxTokenRefresh, sur 401) pour que le
    // single-flight protège les deux chemins à la fois. Le client de refresh est minimal et
    // dédié — jamais CE client — pour casser la récursion (un `/auth/refresh` qui échouerait en
    // 401 ne doit pas redéclencher un refresh de lui-même) et le cycle DI.
    val tokenRefresher =
        TokenRefresher(
            tokenStorage = tokenStorage,
            sessionExpiryNotifier = sessionExpiryNotifier,
            refreshApi = AuthApiImpl(createRefreshHttpClient(baseUrl, refreshEngine)),
        )
    installAuth(tokenStorage, tokenRefresher, baseUrl)
    install(FruxTokenRefresh) {
        this.tokenRefresher = tokenRefresher
    }

    // Non-2xx responses throw instead of failing at deserialization,
    // and 401 is surfaced as a domain-level UnauthorizedException.
    expectSuccess = true
    HttpResponseValidator {
        handleResponseExceptionWithRequest { exception, _ ->
            val clientException =
                exception as? ClientRequestException
                    ?: return@handleResponseExceptionWithRequest
            if (clientException.response.status == HttpStatusCode.Unauthorized) {
                // La notification d'expiration n'est plus décidée ici (#26) : c'est désormais
                // `FruxTokenRefresh` qui la possède, seulement après avoir tenté un refresh et
                // constaté que le serveur le refuse aussi (session réellement morte, pas un 401
                // qu'un jeton neuf peut réparer). Ce validator ne fait plus que traduire le 401 en
                // exception de domaine, quel que soit le contexte de la requête.
                throw UnauthorizedException()
            }
        }
    }

    defaultRequest {
        url(baseUrl)
        header(HttpHeaders.ContentType, ContentType.Application.Json)
        // Capacités annoncées (#617) : sans site-harvests le serveur filtre les récoltes de lieu des
        // listes, sans site-crops il retire les cultures de lieu du référentiel et des lieux (ADR 0036),
        // qu'un client ancien ne saurait pas afficher. Le client de refresh n'en a pas besoin.
        header(ClientFeatures.HEADER, "${ClientFeatures.SITE_HARVESTS}, ${ClientFeatures.SITE_CROPS}")
    }
}

/**
 * Client Ktor minimal dédié à `/auth/refresh` (#26, ADR 0019) : content negotiation + timeouts
 * seulement, ni logging ni auth ni retry ni validator — un refresh est un aller-retour rare et
 * silencieux, pas une requête applicative comme les autres.
 */
private fun createRefreshHttpClient(
    baseUrl: String,
    engine: HttpClientEngine?,
): HttpClient {
    val config: HttpClientConfig<*>.() -> Unit = {
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true })
        }
        install(HttpTimeout) {
            requestTimeoutMillis = 15_000
            connectTimeoutMillis = 10_000
            socketTimeoutMillis = 15_000
        }
        expectSuccess = true
        defaultRequest {
            url(baseUrl)
            header(HttpHeaders.ContentType, ContentType.Application.Json)
        }
    }
    return if (engine != null) HttpClient(engine, config) else HttpClient(config)
}
